import { BN } from '@coral-xyz/anchor';


/// Standard token decimals for the project token and PLATFORM_TOKENS.
const DECIMALS = new BN(6);
/// Scaling factor for fixed-point arithmetic, based on DECIMALS. 10^DECIMALS.
const SCALE = new BN(10).pow(DECIMALS); // 10^6
/// Adjustment scaling factor used in quote calculations.
const ADJUSTMENT_SCALE = new BN(10).pow(new BN(9).sub(DECIMALS)); // 10^3
/// The slope parameter for the price curve.
/// Determines how steeply the price increases. Used to derive curve parameters.
const SLOPE = new BN(40);
/// Maximum number of tokens that can be sold along the bonding curve (supply limit for the curve).
const MAX_X = new BN(600_000_000);
/// MAX_X scaled by the SCALE factor.
const MAX_X_SCALED = MAX_X.mul(SCALE);
/// The threshold of tokens sold that triggers the project status change to Seeding.
const SEEDING_THRESHOLD = new BN(1_000_000); // 1 million tokens
/// SEEDING_THRESHOLD scaled by the SCALE factor.
const SEEDING_THRESHOLD_SCALED = SEEDING_THRESHOLD.mul(SCALE);
/// The amount of project tokens transferred to the seeder for Raydium pool initialization.
const SEED_X_AMOUNT = new BN(200_000_000); // 200 million tokens
/// SEED_X_AMOUNT scaled by the SCALE factor.
const SEED_X_AMOUNT_SCALED = SEED_X_AMOUNT.mul(SCALE);
/// The amount of PLATFORM tokens transferred to the seeder for Raydium pool initialization.
const SEED_PLATFORM_TOKEN_AMOUNT = new BN(2_000_000); // 2 million platform tokens
/// SEED_PLATFORM_TOKEN_AMOUNT scaled by the SCALE factor.
const SEED_PLATFORM_TOKEN_AMOUNT_SCALED = SEED_PLATFORM_TOKEN_AMOUNT.mul(SCALE);
/// The total theoretical supply of the project token.
const TOTAL_SUPPLY = new BN(1_000_000_000);
/// TOTAL_SUPPLY scaled by the SCALE factor.
const TOTAL_SUPPLY_SCALED = TOTAL_SUPPLY.mul(SCALE);

/// Maximum price the token can reach on the bonding curve (at MAX_X). Scaled by SCALE.
/// Corresponds to 0.01 PLATFORM_TOKEN per token.
const MAX_PRICE_SCALED = new BN(10_000_000); // 0.01 * SCALE

/// The y-intercept 'b' of the price curve p(x) = ax^2 + b.
/// Represents the minimum price (at x=0). Scaled by SCALE.
const B = MAX_PRICE_SCALED.div(SLOPE); // 0.00025 * SCALE

/// Precomputed MAX_X * MAX_X.
const MAX_X_SQUARED = MAX_X.mul(MAX_X);
/// Precomputed MAX_X_SCALED * MAX_X_SCALED / SCALE. Used in price calculation denominator.
const MAX_X_SQUARED_SCALED = MAX_X_SCALED.div(SCALE).mul(MAX_X_SCALED); // equivalent to SCALE * MAX_X * MAX_X;
/// Precomputed difference between max price and B, used in price calculation numerator.
const PRICE_DIFFERENCE = MAX_PRICE_SCALED.sub(B);

export const BONDING_CURVE = {
  DECIMALS,
  SCALE,
  ADJUSTMENT_SCALE,
  SLOPE,
  MAX_X,
  MAX_X_SCALED,
  TOTAL_SUPPLY_SCALED,
  SEEDING_THRESHOLD,
  SEEDING_THRESHOLD_SCALED,
  SEED_X_AMOUNT,
  SEED_X_AMOUNT_SCALED,
  SEED_PLATFORM_TOKEN_AMOUNT,
  SEED_PLATFORM_TOKEN_AMOUNT_SCALED,
}

/**
 * Calculates instantaneous price P(x) = a*x^2 + b based on Rust math.
 * Corresponds to Rust's `calculate_price_from_x`.
 * All inputs/outputs are scaled BN values.
 */
export const calculatePriceFromX = (x: BN): BN => {
  return x.mul(x.div(SCALE))
    .mul(PRICE_DIFFERENCE)
    .div(MAX_X_SQUARED_SCALED)
    .add(B)
};

/**
 * Calculates the average price over a token amount range using the integral method.
 * Corresponds to Rust's `calculate_price`.
 * All inputs/outputs are scaled BN values.
 */
export const calculateAveragePrice = (currentSold: BN, amount: BN, isSell: boolean) => {
  if (amount.isZero()) {
    return calculatePriceFromX(currentSold);
  }

  const x1 = isSell ? currentSold.sub(amount) : currentSold;
  const x2 = isSell ? currentSold : currentSold.add(amount);

  // Clamp x1 and x2 to be non-negative
  const priceAtX1 = calculatePriceFromX(x1);
  const priceAtX2 = calculatePriceFromX(x2);
  const aX1Squared = priceAtX1.sub(B); 
  const aX2Squared = priceAtX2.sub(B);

  const x1x2 = x1.mul(x2.div(SCALE));
  const aX1X2 = x1x2
    .mul(PRICE_DIFFERENCE)
    .div(MAX_X_SQUARED_SCALED);

  const sumATerms = aX1Squared.add(aX2Squared).add(aX1X2);
  const avgATerm = sumATerms.div(new BN(3));
  const averagePrice = avgATerm.add(B);

  return averagePrice;
};

/**
 * Calculates the maximum amount of project tokens that can be bought for a given amount of RINGS.
 * Uses binary search to find the token amount whose cost matches the provided RINGS amount.
 * All inputs/outputs are scaled BN values.
 */
export const calculateTokenAmountForRings = (currentSold: BN, amount: BN): BN => {
  if (amount.isZero()) {
    return new BN(0);
  }

  const maxPossibleTokens = TOTAL_SUPPLY_SCALED.sub(currentSold);
  if (maxPossibleTokens.isNeg() || maxPossibleTokens.isZero()) {
      return new BN(0);
  }

  let low = new BN(0);
  let high = maxPossibleTokens;
  let bestTokenAmount = new BN(0);
  const precision = new BN(1); // Smallest unit (1 lamport)

  while (low.lte(high)) {
    const mid = low.add(high).div(new BN(2));

    if (mid.isZero()) {
      // Avoid infinite loop if low=0, high=0 or low=0, high=1
      // Check if spending 1 lamport worth of RINGS is possible
       const costForOne = calculateAveragePrice(currentSold, precision, false).mul(precision).div(SCALE);
       if (costForOne.lte(amount)) {
           bestTokenAmount = precision;
       }
      break;
    }

    const averagePrice = calculateAveragePrice(currentSold, mid, false);
    const cost = averagePrice.mul(mid).div(SCALE);

    if (cost.lte(amount)) {
      // This amount is affordable, try buying more
      bestTokenAmount = mid; // Store this as a potential best
      low = mid.add(precision);
    } else {
      // This amount is too expensive, try buying less
      high = mid.sub(precision);
    }
  }

  // Optional: Final check for the bestTokenAmount + 1 if needed due to precision floor/ceil effects
  // This ensures we get the absolute maximum possible within the budget
  const finalCost = calculateAveragePrice(currentSold, bestTokenAmount, false).mul(bestTokenAmount).div(SCALE);
  const costForOneMore = calculateAveragePrice(currentSold, bestTokenAmount.add(precision), false).mul(bestTokenAmount.add(precision)).div(SCALE);

  if (costForOneMore.lte(amount) && bestTokenAmount.lt(maxPossibleTokens)){
     return bestTokenAmount.add(precision);
  }

  return bestTokenAmount;
};
